import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { productsApi } from '../services/productsApi';
import { dailyMenuApi } from '../services/dailyMenuApi';
import { productionCenterApi } from '../services/productionCenterApi';
import { kitchenApi } from '../services/kitchenApi';
import { isOfflineCajaDevice } from '../utils/serviceWorker';
import { getConnectionStatus, useConnectionStatus } from '../services/connectionStatus';

/**
 * En la caja offline, baja la carta completa al abrir la app para que quede
 * guardada (services/offlineCache.ts) aunque nadie haya abierto Comandear hoy.
 *
 * Sin esto la copia offline solo tenía lo que la cajera ya había visto: el mapa de
 * mesas sí, pero la carta no hasta el primer "Comandear" con red, y el día que se
 * cayera el internet temprano no habría carta que mostrar.
 *
 * Mismos argumentos que usa ComandearModal: la copia se busca por URL exacta.
 * Incluye los modificadores de todos los productos en una sola llamada
 * (`/products/variant-groups`): sin ellos no se puede calcular el precio de un renglón
 * con variantes sin red, y pedirlos producto por producto serían cientos de peticiones.
 *
 * Una vez por sesión y restaurante: App.tsx monta un MainLayout por ruta, así que el
 * hook se vuelve a montar en cada cambio de página, y sin este candado cada
 * navegación en la caja bajaba la carta completa (5 + N peticiones) contra una API
 * que ya anda justa de memoria.
 */
let warmedRestaurantId: string | null = null;

export function useOfflineCatalogWarmup(): void {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const restaurantId = useAppSelector((state) => state.restaurant.currentRestaurant?.id);
  // Si se calentó sin red (arranque offline) no quedó nada nuevo: se reintenta cuando
  // vuelve la conexión.
  const connection = useConnectionStatus();

  useEffect(() => {
    if (!isAuthenticated || !restaurantId || !isOfflineCajaDevice()) return;
    if (connection !== 'online' || warmedRestaurantId === restaurantId) return;
    warmedRestaurantId = restaurantId;

    const warm = async () => {
      const opts = { subscribe: false, forceRefetch: true } as const;
      // Nueva orden (para llevar / domicilio) pide solo los disponibles: sin red esa lista
      // la arma offlineAdapter con esta, para no bajar la carta dos veces.
      dispatch(productsApi.endpoints.getProducts.initiate({ availableOnly: false }, opts));
      // Modificadores de todo el restaurante: sin ellos la caja no puede poner precio a un
      // renglón con variantes cuando no hay red (offlinePricing).
      dispatch(productsApi.endpoints.getAllVariantGroups.initiate(undefined, opts));
      // Y las variantes sueltas (sin grupo), que no vienen en ningún grupo.
      dispatch(productsApi.endpoints.getAllProductVariants.initiate(undefined, opts));
      dispatch(dailyMenuApi.endpoints.getActiveDailyMenus.initiate(undefined, opts));
      dispatch(productionCenterApi.endpoints.getActiveProductionCenters.initiate(undefined, opts));
      dispatch(kitchenApi.endpoints.getKitchenNotes.initiate(undefined, opts));

      const categories = await dispatch(
        productsApi.endpoints.getCategories.initiate({ activeOnly: true }, opts)
      ).unwrap().catch(() => []);
      // Filtrar por categoría en Comandear pide /products/category/{id}.
      categories.forEach((category) => {
        dispatch(productsApi.endpoints.getProductsByCategory.initiate(category.id, opts));
      });

      // Sin red las respuestas salieron de la copia: no cuenta como calentado.
      if (getConnectionStatus() !== 'online') warmedRestaurantId = null;
    };

    void warm();
  }, [isAuthenticated, restaurantId, connection, dispatch]);
}
